import { useEffect, useRef } from 'react';
import type { AnimationItem } from 'lottie-web';

type LottieAnimationProps = {
  /** Lazy import of the animation JSON, so it stays out of the initial bundle. */
  load: () => Promise<{ default: object }>;
  className?: string;
};

/**
 * Plays a Lottie animation (SVG renderer, looping). Ported from the Ember
 * `lottie-animation` modifier; the animation is decorative, so load failures
 * degrade silently.
 *
 * The lottie-web player is imported dynamically (alongside the animation data)
 * so it stays out of the initial bundle — and, since it touches `<canvas>` at
 * module load, out of the eager module graph that node tests pull in when they
 * import the route tree.
 */
export function LottieAnimation({ load, className }: LottieAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let destroyed = false;
    let animation: AnimationItem | undefined;

    void Promise.all([import('lottie-web/build/player/lottie_light'), load()])
      .then(([lottieModule, animationModule]) => {
        if (destroyed || containerRef.current == null) {
          return;
        }
        animation = lottieModule.default.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          // lottie mutates the data it's given, so hand it a clone.
          animationData: structuredClone(animationModule.default),
        });
      })
      .catch(() => {
        // Decorative; degrade silently.
      });

    return () => {
      destroyed = true;
      animation?.destroy();
    };
  }, [load]);

  return <div ref={containerRef} className={className} aria-hidden="true" />;
}
